<script setup lang="ts">
import type { KanbanLog } from '@entities/kanban-comment'
import type { TutorKanbanStage } from '@brighterly/lib-core-types/tutor-kanban/enums'

import { KanbanLogItem, KanbanLogComment, KanbanLogDeadline, KanbanLogValue, KanbanLogAssignee } from '@widgets/kanban-comments'
import { useKanbanLog, useKanbanLogItem } from '@entities/kanban-comment'
import { useListsStore } from '@shared/model'
import { BadgeKanbanStageStatus } from '@shared/ui/kanban'

import TutorKanbanLogStage from './TutorKanbanLogStage.vue'
import TutorKanbanLogPriority from './TutorKanbanLogPriority.vue'
import TutorKanbanLogStageDetails from './TutorKanbanLogStageDetails.vue'
import { TUTOR_KANBAN_STAGE_COLOR } from '../../config/tutor-kanban-stage-color.const'

defineOptions({
  name: 'TutorKanbanLogItem',
})

const props = defineProps<{
  log: KanbanLog
  main: boolean
  last: boolean
}>()

const listsStore = useListsStore()
const stageList = listsStore.getTutor?.stage ?? {}

const { getFieldLabel } = useKanbanLog()

const refLog = computed<KanbanLog>(() => props.log)

const {
  refIsStatus,
  refIsAgent,
  refIsDeadline,
  refIsComment,
} = useKanbanLogItem(refLog)

const refIsPriority = computed<boolean>(() => props.log.field === 'kanban_priority')
const refIsStageDetails = computed<boolean>(() => props.log.field === 'stage_details')

const refStage = computed<string | undefined>(() => props.log.stage ?? props.log.task_status)
</script>

<template>
  <KanbanLogItem
    :log="log"
    :main="main"
    :last="last"
  >
    <template
      v-if="refStage"
      #stage
    >
      <BadgeKanbanStageStatus
        :text="stageList[refStage] ?? refStage"
        :color="TUTOR_KANBAN_STAGE_COLOR[refStage as TutorKanbanStage]"
      />
    </template>
    <TutorKanbanLogStage
      v-if="refIsStatus"
      :actor="log.actor"
      :old-value="log.old_value ?? ''"
      :value="log.value ?? ''"
    />
    <KanbanLogDeadline
      v-else-if="refIsDeadline"
      :actor="log.actor"
      :old-value="log.old_value"
      :value="log.value"
    />
    <KanbanLogAssignee
      v-else-if="refIsAgent"
      :actor="log.actor"
      label="change agent"
      :old-assignee="log.old_agent"
      :new-assignee="log.new_agent"
    />
    <KanbanLogComment
      v-else-if="refIsComment"
      :actor="log.actor"
      :action="log.action"
      :value="log.value"
    />
    <TutorKanbanLogStageDetails
      v-else-if="refIsStageDetails"
      :actor="log.actor"
      :old-value="log.old_value ?? ''"
      :value="log.value ?? ''"
    />
    <TutorKanbanLogPriority
      v-else-if="refIsPriority"
      :actor="log.actor"
      :old-value="log.old_value ?? ''"
      :value="log.value ?? ''"
    />
    <KanbanLogValue
      v-else-if="log.field"
      :actor="log.actor"
      :label="getFieldLabel(log.field)"
      :old-value="log.old_value"
      :value="log.value"
    />
  </KanbanLogItem>
</template>